import { useState, useEffect } from 'react';
import {
  Modal,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Row,
  Col,
  Card,
  CardBody,
  Spinner,
  Badge
} from 'reactstrap';
import FeatherIcon from 'feather-icons-react';
import { useTranslation } from 'react-i18next';
import api from '../helpers/api';

const ToolsModal = ({
  isOpen,
  toggle,
  toolData,
  loading = false,
  onEdit,
  onCheckout,
  onCheckin,
  onWorkOrderClick
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('details');
  const [checkoutHistory, setCheckoutHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const currentUser = JSON.parse(localStorage.getItem('authUser'));

  const conditionMap = {
    'EXCELLENT': {
      label: t('shared.condition_excellent', 'Excellent'),
      color: 'bg-success'
    },
    'GOOD': {
      label: t('shared.condition_good', 'Good'),
      color: 'bg-primary'
    },
    'FAIR': {
      label: t('shared.condition_fair', 'Fair'),
      color: 'bg-warning'
    },
    'POOR': {
      label: t('shared.condition_poor', 'Poor'),
      color: 'bg-danger'
    }
  };

  const statusMap = {
    'AVAILABLE': {
      label: t('business.tools.status_available', 'Available'),
      color: 'bg-success'
    },
    'CHECKED_OUT': {
      label: t('business.tools.status_checked_out', 'Checked Out'),
      color: 'bg-warning'
    },
    'UNDER_MAINTENANCE': {
      label: t('business.tools.status_under_maintenance', 'Under Maintenance'),
      color: 'bg-info'
    },
    'OUT_OF_SERVICE': {
      label: t('business.tools.status_out_of_service', 'Out of Service'),
      color: 'bg-danger'
    }
  };

  const categoryMap = {
    'HAND_TOOLS': t('business.tools.cat_hand_tools', 'Hand Tools'),
    'POWER_TOOLS': t('business.tools.cat_power_tools', 'Power Tools'),
    'MEASURING_INSTRUMENTS': t('business.tools.cat_measuring', 'Measuring Instruments'),
    'SAFETY_EQUIPMENT': t('business.tools.cat_safety', 'Safety Equipment'),
    'LIFTING_RIGGING': t('business.tools.cat_lifting', 'Lifting & Rigging'),
    'WELDING': t('business.tools.cat_welding', 'Welding'),
    'PNEUMATIC_HYDRAULIC': t('business.tools.cat_pneumatic', 'Pneumatic / Hydraulic'),
    'DIAGNOSTIC': t('business.tools.cat_diagnostic', 'Diagnostic'),
    'GENERAL': t('business.tools.cat_general', 'General')
  };

  const activeCondition = toolData?.condition ? (conditionMap[toolData.condition] || conditionMap[toolData.condition.toUpperCase()]) : null;
  const activeStatus = toolData?.status ? statusMap[toolData.status] : null;

  useEffect(() => {
    if (isOpen && activeTab === 'history' && toolData?.id) {
      fetchCheckoutHistory();
    }
  }, [isOpen, activeTab, toolData?.id]);

  useEffect(() => {
    if (isOpen) {
      setActiveTab('details');
    }
  }, [isOpen]);

  const fetchCheckoutHistory = async () => {
    try {
      setLoadingHistory(true);
      const { data } = await api.get(`work-order-tool/history?toolId=${toolData.id}&count=20`);
      setCheckoutHistory(data?.response || []);
    } catch (err) {
      console.error('Error fetching checkout history:', err);
      setCheckoutHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleEdit = () => {
    if (onEdit && toolData) {
      onEdit(toolData);
      toggle();
    }
  };

  const formatDate = (date) => {
    if (!date) return t('shared.n_a', 'N/A');
    if (Array.isArray(date)) {
      const [y, m, d] = date;
      return `${String(d).padStart(2, '0')}/${String(m).padStart(2, '0')}/${y}`;
    }
    try {
      const d = new Date(date);
      if (isNaN(d.getTime())) return date;
      return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
    } catch {
      return date;
    }
  };

  const canCheckIn = () => {
    if (toolData?.status !== 'CHECKED_OUT') return false;
    return checkoutHistory.some(
      h => h.status === 'CHECKED_OUT'
    );
  };

  const getActiveCheckoutId = () => {
    const active = checkoutHistory.find(
      h => h.status === 'CHECKED_OUT'
    );
    return active?.id;
  };

  const renderCheckoutHistoryStatusBadge = (status) => {
    if (status === 'CHECKED_OUT') {
      return <Badge color="warning">{t('business.tools.status_checked_out', 'Checked Out')}</Badge>;
    }
    if (status === 'RETURNED') {
      return <Badge color="success">{t('business.tools.status_returned', 'Returned')}</Badge>;
    }
    return <Badge color="secondary">{status || t('shared.n_a', 'N/A')}</Badge>;
  };

  const getDocumentIcon = (url) => {
    const ext = url?.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'pdf':
        return 'file-text';
      case 'doc':
      case 'docx':
        return 'file';
      case 'xls':
      case 'xlsx':
        return 'grid';
      default:
        return 'file';
    }
  };

  const getFileNameFromUrl = (url) => {
    try {
      const pathname = new URL(url).pathname;
      return decodeURIComponent(pathname.split('/').pop() || 'Document');
    } catch {
      return url?.split('/').pop() || 'Document';
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      size="xl"
      className="modal-dialog-centered"
    >
      {/* Modal Header with gradient background */}
      <div
        className="modal-header border-0"
        style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          padding: '1.5rem 2rem'
        }}
      >
        <div className="w-100">
          <div className="d-flex justify-content-between align-items-start">
            <div className="text-white">
              <h4 className="mb-2 fw-bold">
                <i className="bx bx-wrench me-2"></i>
                {toolData?.name || t('business.components.tools_modal.title_default', 'Tool Details')}
              </h4>
              <div className="d-flex align-items-center gap-3 flex-wrap">
                <span className="badge bg-white bg-opacity-25 text-white">
                  <i className="bx bx-barcode me-1"></i>
                  {toolData?.serialNumber || 'N/A'}
                </span>
                {toolData?.toolNumber && (
                  <span className="badge bg-white bg-opacity-25 text-white">
                    <i className="bx bx-id-card me-1"></i>
                    {toolData.toolNumber}
                  </span>
                )}
                {activeCondition && (
                  <span className={`badge px-3 py-2 ${activeCondition.color}`}
                  style={{ fontSize: '0.75rem', fontWeight: '600' }}
                  >
                    {activeCondition.label}
                  </span>
                )}
                {activeStatus && (
                  <span className={`badge px-3 py-2 ${activeStatus.color}`}
                  style={{ fontSize: '0.75rem', fontWeight: '600' }}
                  >
                    {activeStatus.label}
                  </span>
                )}
              </div>
            </div>
            <div className="d-flex align-items-center gap-2 ms-auto flex-shrink-0">
              {/* Check Out / Check In button */}
              {toolData?.status === 'AVAILABLE' && onCheckout && (
                <button
                  type="button"
                  className="btn btn-sm d-flex align-items-center gap-2 px-3 py-2 border-0 shadow-sm"
                  onClick={() => onCheckout(toolData)}
                  style={{
                    background: '#ffc107',
                    color: '#212529',
                    fontWeight: '500',
                    borderRadius: '8px',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <i className="bx bx-log-out"></i>
                  <span>{t('business.tools.check_out', 'Check Out')}</span>
                </button>
              )}
              {toolData?.status === 'CHECKED_OUT' && onCheckin && canCheckIn() && (
                <button
                  type="button"
                  className="btn btn-sm d-flex align-items-center gap-2 px-3 py-2 border-0 shadow-sm"
                  onClick={() => onCheckin(getActiveCheckoutId())}
                  style={{
                    background: '#198754',
                    color: '#fff',
                    fontWeight: '500',
                    borderRadius: '8px',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <i className="bx bx-log-in"></i>
                  <span>{t('business.tools.check_in', 'Check In')}</span>
                </button>
              )}
              {onEdit && toolData && (
                <button
                  type="button"
                  className="btn btn-sm d-flex align-items-center gap-2 px-3 py-2 border-0 shadow-sm"
                  onClick={handleEdit}
                  title={t('shared.button_edit_tool', 'Edit Tool')}
                  style={{
                    background: 'rgba(255, 255, 255, 0.95)',
                    backdropFilter: 'blur(10px)',
                    color: '#495057',
                    fontWeight: '500',
                    borderRadius: '8px',
                    transition: 'all 0.2s ease',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = 'rgba(255, 255, 255, 1)';
                    e.target.style.transform = 'translateY(-1px)';
                    e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = 'rgba(255, 255, 255, 0.95)';
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                  }}
                >
                  <FeatherIcon icon="edit-3" size={16} />
                  <span>{t('shared.button_edit_tool', 'Edit Tool')}</span>
                </button>
              )}
              <button
                type="button"
                className="btn-close btn-close-white"
                onClick={toggle}
                aria-label={t('shared.close', 'Close')}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Navigation */}
      <Nav
        tabs
        className="border-bottom-0 px-4 pt-3 bg-light"
        style={{ borderRadius: '0' }}
      >
        <NavItem>
          <NavLink
            className={`nav-link-modern ${activeTab === 'details' ? 'active' : ''}`}
            onClick={() => setActiveTab('details')}
            style={{
              border: 'none',
              borderRadius: '8px 8px 0 0',
              padding: '12px 20px',
              fontWeight: '500',
              transition: 'all 0.3s ease',
              cursor: 'pointer'
            }}
          >
            <i className="bx bx-info-circle me-2"></i>
            {t('shared.details', 'Details')}
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink
            className={`nav-link-modern ${activeTab === 'history' ? 'active' : ''}`}
            onClick={() => setActiveTab('history')}
            style={{
              border: 'none',
              borderRadius: '8px 8px 0 0',
              padding: '12px 20px',
              fontWeight: '500',
              transition: 'all 0.3s ease',
              cursor: 'pointer'
            }}
          >
            <i className="bx bx-history me-2"></i>
            {t('business.components.tools_modal.checkout_history', 'Checkout History')}
          </NavLink>
        </NavItem>
      </Nav>

      {/* Modal Body */}
      <div
        className="modal-body p-0"
        style={{
          maxHeight: '60vh',
          overflow: 'auto'
        }}
      >
        <TabContent activeTab={activeTab} className="h-100">
          {/* Details Tab */}
          <TabPane tabId="details">
            <div className="p-4">
              <Row className="g-4">
                {/* Left Card: Basic Information */}
                <Col md={6}>
                  <Card className="border-0 shadow-sm h-100">
                    <CardBody className="p-4">
                      <h6 className="text-primary mb-3 fw-semibold">
                        <i className="bx bx-info-circle me-2"></i>{t('shared.basic_information', 'Basic Information')}
                      </h6>
                      <div className="info-item mb-3">
                        <label className="text-muted small">{t('shared.tool_name', 'Tool Name')}</label>
                        <div className="fw-medium">{toolData?.name || 'N/A'}</div>
                      </div>
                      {toolData?.toolNumber && (
                        <div className="info-item mb-3">
                          <label className="text-muted small">{t('business.tools.tool_id', 'Tool ID')}</label>
                          <div className="fw-medium">{toolData.toolNumber}</div>
                        </div>
                      )}
                      <div className="info-item mb-3">
                        <label className="text-muted small">{t('shared.serial_number', 'Serial Number')}</label>
                        <div className="fw-medium">{toolData?.serialNumber || 'N/A'}</div>
                      </div>
                      <div className="info-item mb-3">
                        <label className="text-muted small">{t('shared.location', 'Location')}</label>
                        <div className="fw-medium">{toolData?.location || 'N/A'}</div>
                      </div>
                      <div className="info-item mb-3">
                        <label className="text-muted small">{t('shared.condition', 'Condition')}</label>
                        <div>
                          {activeCondition ? (
                            <span className={`badge px-3 py-2 ${activeCondition.color}`}
                            style={{ fontSize: '0.85rem', fontWeight: '400' }}>
                              {activeCondition.label}
                            </span>
                          ) : toolData?.condition ? (
                            <span className="badge px-3 py-2 bg-secondary" style={{ fontSize: '0.85rem', fontWeight: '400' }}>
                              {toolData.condition}
                            </span>
                          ) : (
                            <span className="text-muted">{t('shared.n_a', 'N/A')}</span>
                          )}
                        </div>
                      </div>
                      <div className="info-item mb-0">
                        <label className="text-muted small">{t('shared.status', 'Status')}</label>
                        <div>
                          {activeStatus ? (
                            <span className={`badge px-3 py-2 ${activeStatus.color}`}
                            style={{ fontSize: '0.85rem', fontWeight: '400' }}>
                              {activeStatus.label}
                            </span>
                          ) : (
                            <span className="text-muted">{t('shared.n_a', 'N/A')}</span>
                          )}
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                </Col>

                {/* Right Card: Classification & Financial */}
                <Col md={6}>
                  <Card className="border-0 shadow-sm h-100">
                    <CardBody className="p-4">
                      <h6 className="text-info mb-3 fw-semibold">
                        <i className="bx bx-category me-2"></i>{t('business.components.tools_modal.classification_financial', 'Classification & Financial')}
                      </h6>
                      <div className="info-item mb-3">
                        <label className="text-muted small">{t('business.tools.category', 'Category')}</label>
                        <div className="fw-medium">
                          {toolData?.category ? (categoryMap[toolData.category] || toolData.category.replace(/_/g, ' ')) : t('shared.n_a', 'N/A')}
                        </div>
                      </div>
                      <div className="info-item mb-3">
                        <label className="text-muted small">{t('business.tools.manufacturer', 'Manufacturer')}</label>
                        <div className="fw-medium">{toolData?.manufacturer || t('shared.n_a', 'N/A')}</div>
                      </div>
                      <div className="info-item mb-3">
                        <label className="text-muted small">{t('business.tools.model', 'Model')}</label>
                        <div className="fw-medium">{toolData?.model || t('shared.n_a', 'N/A')}</div>
                      </div>
                      {toolData?.vendor && (
                        <div className="info-item mb-3">
                          <label className="text-muted small">{t('business.tools.vendor', 'Vendor')}</label>
                          <div className="fw-medium">{toolData.vendor.vendorName || toolData.vendor.name || t('shared.n_a', 'N/A')}</div>
                        </div>
                      )}
                      <div className="info-item mb-3">
                        <label className="text-muted small">{t('shared.cost', 'Cost')}</label>
                        <div className="fw-medium">
                          {toolData?.organization?.currency || toolData?.currency || 'USD'} {toolData?.cost?.toLocaleString() || 0}
                        </div>
                      </div>
                      <div className="info-item mb-0">
                        <label className="text-muted small">{t('business.tools.purchase_date', 'Purchase Date')}</label>
                        <div className="fw-medium">{formatDate(toolData?.purchaseDate)}</div>
                      </div>
                    </CardBody>
                  </Card>
                </Col>

                {/* Description (own row) */}
                {toolData?.description && (
                  <Col md={12}>
                    <Card className="border-0 shadow-sm">
                      <CardBody className="p-4">
                        <h6 className="text-primary mb-3 fw-semibold">
                          <i className="bx bx-text me-2"></i>{t('shared.description', 'Description')}
                        </h6>
                        <div className="fw-medium">{toolData.description}</div>
                      </CardBody>
                    </Card>
                  </Col>
                )}

                {/* Image (own row) */}
                {toolData?.imageUrl && (
                  <Col md={12}>
                    <Card className="border-0 shadow-sm">
                      <CardBody className="p-4">
                        <h6 className="text-primary mb-3 fw-semibold">
                          <i className="bx bx-image me-2"></i>{t('business.tools.image', 'Image')}
                        </h6>
                        <img
                          src={toolData.imageUrl}
                          alt={toolData.name || 'Tool'}
                          className="rounded border"
                          style={{ maxHeight: '200px', objectFit: 'cover', cursor: 'pointer' }}
                          onClick={() => window.open(toolData.imageUrl, '_blank')}
                        />
                      </CardBody>
                    </Card>
                  </Col>
                )}

                {/* Documents (own row) */}
                {toolData?.documentUrls?.length > 0 && (
                  <Col md={12}>
                    <Card className="border-0 shadow-sm">
                      <CardBody className="p-4">
                        <h6 className="text-warning mb-3 fw-semibold">
                          <i className="bx bx-file me-2"></i>{t('shared.documents', 'Documents')}
                        </h6>
                        <div className="d-flex flex-column gap-2">
                          {toolData.documentUrls.map((url, index) => (
                            <div key={index} className="d-flex align-items-center p-2 bg-light rounded">
                              <div className="d-flex align-items-center justify-content-center me-2"
                                   style={{ width: '32px', height: '32px', backgroundColor: '#e9ecef', borderRadius: '4px' }}>
                                <FeatherIcon icon={getDocumentIcon(url)} size={16} className="text-primary" />
                              </div>
                              <div className="flex-grow-1 overflow-hidden">
                                <p className="mb-0 text-truncate small fw-medium">{getFileNameFromUrl(url)}</p>
                                <small className="text-muted">{url.split('.').pop()?.toUpperCase()}</small>
                              </div>
                              <a
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-sm btn-outline-primary"
                              >
                                <FeatherIcon icon="download" size={12} />
                              </a>
                            </div>
                          ))}
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                )}
              </Row>
            </div>
          </TabPane>

          {/* Checkout History Tab */}
          <TabPane tabId="history">
            <div className="p-4">
              <Card className="border-0 shadow-sm">
                <CardBody className="p-4">
                  <div className="d-flex align-items-center mb-4">
                    <div className="info-icon me-3">
                      <div className="bg-warning bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center"
                           style={{ width: '40px', height: '40px' }}>
                        <i className="bx bx-history text-warning fs-5"></i>
                      </div>
                    </div>
                    <div>
                      <h5 className="mb-1 fw-bold">{t('business.components.tools_modal.checkout_history', 'Checkout History')}</h5>
                      <p className="text-muted small mb-0">{t('business.components.tools_modal.checkout_history_subtitle', 'Track all checkout and return activity')}</p>
                    </div>
                  </div>

                  {loadingHistory ? (
                    <div className="text-center py-5">
                      <Spinner color="primary" size="lg" />
                      <p className="text-muted mt-3">{t('shared.loading', 'Loading...')}</p>
                    </div>
                  ) : checkoutHistory.length > 0 ? (
                    <div className="table-responsive">
                      <table className="table table-hover mb-0">
                        <thead className="bg-light">
                          <tr>
                            <th>{t('shared.date', 'Date')}</th>
                            <th>{t('shared.user', 'User')}</th>
                            <th>{t('shared.work_order', 'Work Order')}</th>
                            <th>{t('business.tools.condition_out', 'Condition Out')}</th>
                            <th>{t('business.tools.condition_return', 'Condition Return')}</th>
                            <th>{t('shared.status', 'Status')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {checkoutHistory.map((entry, index) => (
                            <tr key={entry.id || index}>
                              <td>
                                <small>{formatDate(entry.checkedOutAt)}</small>
                              </td>
                              <td>
                                <small>{entry.checkedOutByUser?.name || entry.user?.name || entry.checkedOutByName || t('shared.n_a', 'N/A')}</small>
                              </td>
                              <td>
                                {entry.workOrderTicketNumber ? (
                                  <small
                                    className="text-primary"
                                    style={{ cursor: 'pointer', textDecoration: 'underline' }}
                                    onClick={() => onWorkOrderClick && onWorkOrderClick(entry.workOrderTicketNumber)}
                                  >
                                    {entry.workOrderTitle || entry.workOrderTicketNumber}
                                  </small>
                                ) : (
                                  <small>-</small>
                                )}
                              </td>
                              <td>
                                {entry.conditionAtCheckout ? (
                                  <Badge color={conditionMap[entry.conditionAtCheckout]?.color === 'bg-success' ? 'success' :
                                    conditionMap[entry.conditionAtCheckout]?.color === 'bg-primary' ? 'primary' :
                                    conditionMap[entry.conditionAtCheckout]?.color === 'bg-warning' ? 'warning' : 'danger'}
                                    className="badge-soft" style={{ fontSize: '11px' }}>
                                    {conditionMap[entry.conditionAtCheckout]?.label || entry.conditionAtCheckout}
                                  </Badge>
                                ) : '-'}
                              </td>
                              <td>
                                {entry.conditionAtReturn ? (
                                  <Badge color={conditionMap[entry.conditionAtReturn]?.color === 'bg-success' ? 'success' :
                                    conditionMap[entry.conditionAtReturn]?.color === 'bg-primary' ? 'primary' :
                                    conditionMap[entry.conditionAtReturn]?.color === 'bg-warning' ? 'warning' : 'danger'}
                                    className="badge-soft" style={{ fontSize: '11px' }}>
                                    {conditionMap[entry.conditionAtReturn]?.label || entry.conditionAtReturn}
                                  </Badge>
                                ) : '-'}
                              </td>
                              <td>{renderCheckoutHistoryStatusBadge(entry.status)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-5">
                      <div className="empty-state-icon d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                           style={{ width: '64px', height: '64px', backgroundColor: '#f8f9fa' }}>
                        <i className="bx bx-history text-muted fs-3"></i>
                      </div>
                      <h6 className="text-muted">{t('business.components.tools_modal.no_checkout_history', 'No Checkout History')}</h6>
                      <p className="text-muted mb-0">{t('business.components.tools_modal.no_checkout_history_desc', 'This tool has not been checked out yet')}</p>
                    </div>
                  )}
                </CardBody>
              </Card>
            </div>
          </TabPane>
        </TabContent>
      </div>

      {/* Modal Footer */}
      <div className="modal-footer border-top bg-light">
        <button
          type="button"
          className="btn btn-secondary"
          onClick={toggle}
        >
          <i className="bx bx-x me-1"></i>
          {t('shared.close', 'Close')}
        </button>
      </div>
    </Modal>
  );
};

export default ToolsModal;
